import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import {
  BRAND_PLANS_PATH,
  BRAND_SERVICES_PATH,
  ROUTES,
  clientDetailPath,
} from "../../../routes/paths";
import PageHeader from "../../../components/layout/PageHeader";
import Pagination from "../../../components/ui/Pagination";
import {
  Button,
  EmptyStateProps,
  Tabs,
  TabItem,
  failedEmptyState,
  searchEmptyState,
  useLoadPhase,
} from "../../../components/ui";
// The one screen-sized illustration on this page, and only for the workspace
// that has never added a client — see `EmptyState`'s `welcome` variant.
import { ReactComponent as FirstClientMark } from "../../../assets/svg/No data images/my client.svg";
import ClientsTable from "../components/ClientsTable";
import { toClientRow } from "../clientMapping";
import { countClientNews } from "../clientNews";
import { clientsNewsSince } from "../openedClients";
import { useClientLists } from "../hooks/useClients";
import { useOpenedClients } from "../hooks/useOpenedClients";
import { useWaitingSubmissions } from "../hooks/useWaitingSubmissions";
// A deep import rather than the billing barrel: that barrel pulls in the
// checkout and result screens, and this page only needs to know whether the
// workspace is allowed to add anything.
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
// Deep imports for the same reason: the brand barrel pulls in the whole My
// brand screen, and this page only needs to know whether the workspace has
// said what it sells and what it does, and how to say so if it has not.
import PlansRequiredModal from "../../brand/components/PlansRequiredModal";
import ServicesRequiredModal from "../../brand/components/ServicesRequiredModal";
import { usePlansCreated } from "../../brand/hooks/usePlansCreated";
import { useServicesCreated } from "../../brand/hooks/useServicesCreated";
import { useSetupGate } from "../../brand/hooks/useSetupGate";
import { ClientTabId } from "../types/clients.types";

const CLIENTS_PER_PAGE = 10;

/**
 * The tabs in the order they are drawn, and what each one calls itself.
 *
 * Active first because it is the list a dietitian opens this screen for, then
 * New — the clients on the books who have not bought anything yet, and so the
 * ones with something outstanding to do about them — and Old last.
 */
const TAB_ORDER: ClientTabId[] = ["active", "new", "old"];

const TAB_COPY: Record<ClientTabId, { label: string; cardTitle: string }> = {
  active: { label: "Active", cardTitle: "Total active clients" },
  new: { label: "New", cardTitle: "Total new clients" },
  old: { label: "Old clients", cardTitle: "Total old clients" },
};

const MyClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ClientTabId>("active");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Three lists, one request each — the backend's `?status=` is what decides
  // which tab a client lands in now. See `useClientLists`.
  const { lists, reload } = useClientLists();
  const { clients, status } = lists[activeTab];

  // Which rows are carrying a flag, so a tab can say it is holding one without
  // being opened. The same two readings the rows themselves draw — a client
  // who has landed unopened, and answers that have come back unread — counted
  // per list. See `clientNews`.
  const seen = useOpenedClients();
  const since = clientsNewsSince();
  const waiting = useWaitingSubmissions();

  // Clients are one of the three things a plan is what pays for, so with no
  // plan running both ways to Add client are off. The roster itself stays
  // readable. See `useSubscriptionGate`.
  const gate = useSubscriptionGate();

  // And a client is added holding one of the workspace's own plans and working
  // towards one of its services, so a workspace short of either has nothing to
  // add them against: the Plan field or the Goal field would open on an empty
  // list. Both ways to Add client go through the prompt instead — see
  // `useSetupGate`.
  //
  // `status === "ready"` is load-bearing on both: a read still out, or one that
  // failed, leaves the door open rather than standing this in front of somebody
  // who has been selling for a year.
  const { plansCreated, status: plansStatus } = usePlansCreated();
  const { servicesCreated, status: servicesStatus } = useServicesCreated();
  const noPlans = plansStatus === "ready" && !plansCreated;
  const noServices = servicesStatus === "ready" && !servicesCreated;
  const setup = useSetupGate(noPlans || noServices);

  const addClient = setup.guard(() => navigate(ROUTES.addClient));

  // A list that lands quickly goes straight from blank to filled: the table's
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(status === "loading");

  // No splitting left to do: the open tab's list is the one the server was
  // asked for. This used to be a filter on `archived_at` over a single
  // unfiltered read, which could only ever tell two lists apart — a client who
  // had never bought anything was filed among the active ones.
  const source = useMemo(() => clients.map(toClientRow), [clients]);

  // Searching and paging still happen here rather than on the server: the
  // screen counts each tab and searches inside one, so it holds the list it is
  // showing whole. Both move server-side the day the counts come with it.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return source;
    return source.filter(
      (client) =>
        client.name.toLowerCase().includes(query) ||
        // Any one of them matching is a hit: a client working towards three
        // things should be found by whichever of the three was typed.
        client.goals.some((goal) => goal.toLowerCase().includes(query))
    );
  }, [source, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / CLIENTS_PER_PAGE));
  // Clamp rather than reset: a filter that shrinks the list past the current
  // page should land on the last page, not throw away the user's position.
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice(
    (currentPage - 1) * CLIENTS_PER_PAGE,
    currentPage * CLIENTS_PER_PAGE
  );

  // No counts until each list is in: a "0" beside a tab that is still loading
  // reads as an answer rather than as a blank. Each tab answers for its own
  // read, so a slow one is the only one left blank.
  const tabs: TabItem<ClientTabId>[] = TAB_ORDER.map((tab) => ({
    id: tab,
    label: TAB_COPY[tab].label,
    count:
      lists[tab].status === "ready" ? lists[tab].clients.length : undefined,
    // On any tab holding something unlooked-at, not just New: a client who has
    // been on a plan for a month and has just sent their pre-consultation
    // answers back is news, and their row is under Active. A tab still loading
    // says nothing, for the same reason it shows no count.
    dot:
      lists[tab].status === "ready" &&
      countClientNews(lists[tab].clients, seen, since, waiting) > 0,
  }));

  // A workspace that has never added anybody, as opposed to one tab that
  // happens to be empty — all three have to have answered to know that.
  const rosterEmpty = TAB_ORDER.every(
    (tab) => lists[tab].status === "ready" && lists[tab].clients.length === 0
  );

  const handleTabChange = (tab: ClientTabId) => {
    setActiveTab(tab);
    setSearch("");
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  /** What the table shows in place of rows, whichever way it came to have none.
   *  The ways are genuinely different and get genuinely different answers: a
   *  failed fetch, a search that was too narrow, a workspace with no clients in
   *  it at all, and a single tab that is empty while the other two have rows.
   *
   *  Nothing about loading here: while the list is out the table shimmers a
   *  page of rows, and the status line above is what says so in words. */
  const emptyState = (): EmptyStateProps => {
    if (status === "failed")
      return failedEmptyState(
        "We could not load your clients",
        <Button size="sm" variant="secondary" onClick={reload}>
          Try again
        </Button>
      );

    if (search.trim()) return searchEmptyState("clients");

    // One empty tab out of three is a list reporting a result, not a workspace
    // with nothing in it — so it says where the clients actually are rather
    // than inviting anyone to add a first one. An empty list of clients is
    // exactly what a nutritionist would fear seeing, and each of these says
    // plainly that nothing has been lost.
    if (!rosterEmpty && activeTab !== "active")
      return activeTab === "old"
        ? {
            title: "No old clients yet",
            description:
              "Clients whose plans have ended collect here on their own. Nothing has been archived yet.",
            action: (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleTabChange("active")}
              >
                Go to active clients
              </Button>
            ),
          }
        : {
            title: "No new clients right now",
            description:
              "A client you have added but not yet sold a plan to waits here. Everyone on your books is on one.",
            action: (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleTabChange("active")}
              >
                Go to active clients
              </Button>
            ),
          };

    // The Active tab empty while the others have rows: nobody is on a running
    // plan, which is a state worth naming rather than papering over with the
    // first-run welcome.
    if (!rosterEmpty)
      return {
        title: "No active clients right now",
        description:
          "Nobody has a plan running at the moment. Your other clients are under the New and Old clients tabs.",
      };

    // Nothing in any of the three lists is a workspace on its first day rather
    // than a list reporting a result, so this one is the welcome layout: what
    // the screen is for, then the one thing to do about it.
    return {
      variant: "welcome",
      // Sized here rather than left at the file's own 60px: the two welcome
      // marks in the panel are drawn at different sizes, and the class is what
      // makes this one and the enquiries one match on screen.
      illustration: <FirstClientMark aria-hidden className="w-11 h-11" />,
      title: "Add your first client",
      description: "Create your client list to get started.",
      highlights: {
        title: "Your clients, organised",
        items: [
          "Keep every client profile organised in one place.",
          "Create & share personalised meal plans.",
          "Automatic renewal reminders for you and your clients.",
        ],
      },
      action: (
        <Button
          size="md"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={addClient}
          {...gate.props("add a client")}
        >
          Add client
        </Button>
      ),
    };
  };

  return (
    // The page owns the viewport height instead of growing past it, so the
    // only thing that scrolls is the table body inside the card.
    <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="My clients" />

      <div className="mt-5 flex-shrink-0 flex items-end justify-between gap-4 border-b border-line">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
        <Button
          size="sm"
          startIcon={<Plus className="w-4 h-4" />}
          className="mb-3"
          onClick={addClient}
          {...gate.props("add a client")}
        >
          Add client
        </Button>
      </div>

      <section className="mt-5 flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6">
          <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
            {TAB_COPY[activeTab].cardTitle}
          </h2>

          <div className="relative w-full sm:w-[260px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
            <input
              type="search"
              value={search}
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder="Search"
              aria-label="Search clients"
              className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
            />
          </div>
        </div>

        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: this line is not seen, so it costs nothing to be
            early, and the skeleton rows it stands for are hidden from screen
            readers — they are the shape of an answer rather than the answer. */}
        {status === "loading" && (
          <p role="status" className="sr-only">
            Loading your clients…
          </p>
        )}

        <ClientsTable
          clients={visible}
          phase={phase}
          // Activity is the running plan's length, and an old client has no
          // plan running — that tab drops the column rather than show a list of
          // dashes. The New tab keeps it: a client who has just bought their
          // first plan has a length to report, and the column is what the tab
          // being "same as Active" means.
          showActivity={activeTab !== "old"}
          onSelectClient={(client) => navigate(clientDetailPath(client.id))}
          empty={emptyState()}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setPage}
          resultCount={visible.length}
        />
      </section>

      {/* Not a screen of its own: the roster behind it is perfectly readable,
          and the person may well have come here to look at it rather than to
          add anybody. "Not now" leaves them on it.

          The plan is asked for first where both are missing, so a workspace on
          its first day is walked through them in the order the My brand tabs
          hold them rather than shown two dialogs at once. */}
      {setup.prompting &&
        (noPlans ? (
          <PlansRequiredModal
            onCreatePlan={() => navigate(BRAND_PLANS_PATH)}
            onDismiss={setup.dismiss}
          />
        ) : (
          <ServicesRequiredModal
            onCreateService={() => navigate(BRAND_SERVICES_PATH)}
            onDismiss={setup.dismiss}
          />
        ))}
    </div>
  );
};

export default MyClientsPage;
